import * as THREE from 'three';

export type MeasureAxis = 'free' | 'x' | 'y' | 'z';

/** Owns the viewport markers, distance line, and floating label for point-to-point measurement. */
export class MeasurementOverlay {
  public readonly group = new THREE.Group();
  public readonly markerA: THREE.Mesh;
  public readonly markerB: THREE.Mesh;
  public readonly previewMarker: THREE.Mesh;
  public readonly line: THREE.Line;
  public readonly label: HTMLDivElement;
  public midpoint: THREE.Vector3 | null = null;

  constructor(scene: THREE.Scene, container: HTMLElement) {
    this.group.name = 'MeasurementToolGroup';
    scene.add(this.group);
    const markerGeo = new THREE.SphereGeometry(0.45, 16, 16);
    this.markerA = new THREE.Mesh(markerGeo, new THREE.MeshBasicMaterial({ color: 0xef4444, depthTest: false }));
    this.markerB = new THREE.Mesh(markerGeo, new THREE.MeshBasicMaterial({ color: 0x10b981, depthTest: false }));
    this.previewMarker = new THREE.Mesh(markerGeo, new THREE.MeshBasicMaterial({ color: 0x3b82f6, depthTest: false, opacity: 0.7, transparent: true }));
    for (const marker of [this.markerA, this.markerB, this.previewMarker]) {
      marker.renderOrder = 9999;
      marker.visible = false;
    }
    this.group.add(this.markerA, this.markerB, this.previewMarker);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
    this.line = new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: 0x2563eb, linewidth: 1, depthTest: false }));
    this.line.renderOrder = 9998;
    this.line.visible = false;
    this.group.add(this.line);

    this.label = document.createElement('div');
    this.label.style.cssText = `
      position: absolute; transform: translate(-50%, -50%); background: #0f172a; color: #38bdf8;
      border: 1px solid #3b82f6; border-radius: 4px; padding: 3px 8px;
      font-family: var(--mono-font, monospace); font-size: 11px; font-weight: 700;
      z-index: 1000; pointer-events: none; display: none; white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3); letter-spacing: 0.02em;
    `;
    container.appendChild(this.label);
  }

  public reset(): void {
    this.midpoint = null;
    this.markerA.visible = false;
    this.markerB.visible = false;
    this.previewMarker.visible = false;
    this.line.visible = false;
    this.label.style.display = 'none';
  }

  public updateLine(a: THREE.Vector3, b: THREE.Vector3): void {
    const position = this.line.geometry.getAttribute('position') as THREE.BufferAttribute;
    position.setXYZ(0, a.x, a.y, a.z);
    position.setXYZ(1, b.x, b.y, b.z);
    position.needsUpdate = true;
    this.line.geometry.computeBoundingSphere();
    this.line.visible = true;
  }

  public updateLabel(a: THREE.Vector3, b: THREE.Vector3): void {
    this.midpoint = new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5);
    this.label.textContent = `${a.distanceTo(b).toFixed(2)} mm`;
    this.label.style.display = 'block';
  }
}

/** Project a camera ray onto the world-axis line through the first measurement point. */
export function projectRayToAxis(
  ray: THREE.Ray,
  origin: THREE.Vector3,
  axis: Exclude<MeasureAxis, 'free'>,
): THREE.Vector3 | null {
  const axisDir = axis === 'x' ? new THREE.Vector3(1, 0, 0)
    : axis === 'y' ? new THREE.Vector3(0, 1, 0)
      : new THREE.Vector3(0, 0, 1);
  const rayDir = ray.direction;
  const offset = new THREE.Vector3().subVectors(ray.origin, origin);
  const a = rayDir.dot(rayDir);
  const b = rayDir.dot(axisDir);
  const c = axisDir.dot(axisDir);
  const d = rayDir.dot(offset);
  const e = axisDir.dot(offset);
  const denominator = a * c - b * b;
  if (Math.abs(denominator) <= 1e-6) return null;
  const distanceAlongAxis = (a * e - b * d) / denominator;
  return origin.clone().addScaledVector(axisDir, distanceAlongAxis);
}

/** Constrain a picked second point to the selected world axis through the first point. */
export function constrainPointToAxis(
  pointA: THREE.Vector3,
  pickedPoint: THREE.Vector3,
  axis: MeasureAxis,
): THREE.Vector3 {
  if (axis === 'free') return pickedPoint.clone();
  if (axis === 'x') return new THREE.Vector3(pickedPoint.x, pointA.y, pointA.z);
  if (axis === 'y') return new THREE.Vector3(pointA.x, pickedPoint.y, pointA.z);
  return new THREE.Vector3(pointA.x, pointA.y, pickedPoint.z);
}

/** Snap a triangle hit to its nearest vertex or edge midpoint when within the tolerance. */
export function snapToClosestVertexOrEdge(
  intersection: THREE.Intersection,
  snapThreshold = 3.5,
): THREE.Vector3 {
  const rawPoint = intersection.point.clone();
  const mesh = intersection.object as THREE.Mesh;
  const face = intersection.face;
  if (!mesh?.geometry || !face) return rawPoint;

  const position = mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!position) return rawPoint;
  const world = mesh.matrixWorld;
  const a = new THREE.Vector3().fromBufferAttribute(position, face.a).applyMatrix4(world);
  const b = new THREE.Vector3().fromBufferAttribute(position, face.b).applyMatrix4(world);
  const c = new THREE.Vector3().fromBufferAttribute(position, face.c).applyMatrix4(world);
  const candidates = [
    a, b, c,
    new THREE.Vector3().addVectors(a, b).multiplyScalar(0.5),
    new THREE.Vector3().addVectors(b, c).multiplyScalar(0.5),
    new THREE.Vector3().addVectors(c, a).multiplyScalar(0.5),
  ];
  let closest = rawPoint;
  let closestDistance = snapThreshold;
  for (const candidate of candidates) {
    const distance = rawPoint.distanceTo(candidate);
    if (distance <= closestDistance) {
      closest = candidate;
      closestDistance = distance;
    }
  }
  return closest.clone();
}
